import {Injectable} from "@angular/core";
import {MenuItem} from "primeng/api";
import {
    AnalyticalMetric,
    ChildNodes,
    DeviceMetricNode,
    DeviceNode,
    MenuDeviceMetricNodes,
    MenuDeviceNodes,
    Metric,
} from "../model/constructor-menu-nodes";
import {MenuDeviceMetricNodesDto, MenuDeviceNodesDto} from "../api/widget-constructor.service";
import {WidgetElement} from "@entities/widget-element";
import {GridsterItem, GridsterItemComponentInterface} from "angular-gridster2";

@Injectable({providedIn: "root"})
export class WidgetConstructorMapperService {
    mapDtoToMenuDeviceNodes(dto: MenuDeviceNodesDto): MenuDeviceNodes {
        const nodes: MenuDeviceNodes = {};

        this.addDeviceNodesRecursively(nodes, dto);

        return nodes;
    }

    mapDtoToMenuMetricNodes(dto: MenuDeviceMetricNodesDto): MenuDeviceMetricNodes {
        const nodes: MenuDeviceMetricNodes = {};

        this.addDeviceMetricNodesRecursively(nodes, dto);

        return nodes;
    }

    mapDtoTopLevelNodesIds(nodes: MenuDeviceNodesDto | MenuDeviceMetricNodesDto): string[] {
        return nodes.map(node => node.id);
    }

    mapDtoToTopLevelChildNodes(dto: MenuDeviceNodesDto | MenuDeviceMetricNodesDto): ChildNodes {
        return dto.map(node => ({
            id: node.id,
            name: node.name
        }));
    }

    mapDeviceNodesToMenuItems(nodes: DeviceNode[], command: (node: DeviceNode) => void, disabledId?: string | null): MenuItem[] {
        return [{
            label: "Устройства",
            items: nodes.map(node => ({
                id: node.id,
                label: node.name,
                disabled: node.id === disabledId,
                command: () => command(node),
            }))
        }];
    }

    concatMenuItems(...items: MenuItem[]) {
        return items.filter(item => item.items?.length);
    }

    mapMetricNodesToMenuItem(nodes: DeviceMetricNode[], deviceCommand: (node: DeviceMetricNode) => void, disabledIds: Array<string | null>): MenuItem {
        return {
            label: "Устройства",
            items: nodes.map(node => ({
                label: node.name,
                disabled: disabledIds?.includes(node.id),
                command: () => deviceCommand(node),
            }))
        };
    }

    mapMetricsToMenuItem(metrics: Metric[], metricCommand: (metric: Metric) => void, disabledIds: Array<string | null>): MenuItem {
        return {
            label: "Метрики",
            items: metrics.map(metric => ({
                label: metric.name,
                disabled: disabledIds?.includes(metric.id),
                command: () => metricCommand(metric)
            }))
        };
    }

    mapAnalyticalMetricsToMenuItem(analyticalMetrics: AnalyticalMetric[], analyticalMetricCommand: (metric: AnalyticalMetric) => void, disabledIds: Array<string | null>): MenuItem {
        return {
            label: "Аналитические метрики",
            items: analyticalMetrics.map(metric => ({
                label: metric.name,
                disabled: disabledIds?.includes(metric.id),
                command: () => analyticalMetricCommand(metric)
            }))
        };
    }

    mapWidgetElementsToGridsterItems(elements: WidgetElement[]): GridsterItem[] {
        return elements.map(el => ({...el, cols: el.w, rows: el.h}));
    }

    mapGridsterItemComponentsToWidgetElements(items: GridsterItemComponentInterface[]): WidgetElement[] {
        return items.map(item => ({
            id: item.item["id"],
            x: item.item.x,
            y: item.item.y,
            w: item.item.cols,
            h: item.item.rows,
            data: item.item["data"]
        }));
    }

    mapIdsToDeviceNodes(ids: string[], deviceNodes: MenuDeviceNodes): DeviceNode[] {
        return ids.map(id => deviceNodes[id]);
    }

    mapIdsToMetricNodes(ids: string[], metricNodes: MenuDeviceMetricNodes): DeviceMetricNode[] {
        return ids.map(id => metricNodes[id]);
    }

    private addDeviceNodesRecursively(nodes: MenuDeviceNodes, dto: MenuDeviceNodesDto) {
        dto.forEach(node => {
            nodes[node.id] = {
                id: node.id,
                name: node.name,
                nodes: node.nodes.map(child => ({id: child.id, name: child.name})),
            };

            if (node.nodes.length) {
                this.addDeviceNodesRecursively(nodes, node.nodes);
            }
        });
    }

    private addDeviceMetricNodesRecursively(nodes: MenuDeviceMetricNodes, dto: MenuDeviceMetricNodesDto) {
        dto.forEach(node => {
            nodes[node.id] = {
                id: node.id,
                name: node.name,
                metrics: node.metrics.map(m => ({
                    id: m.id,
                    name: m.local_name,
                    unit: m.unit,
                })),
                analyticalMetrics: node.analytical_metrics.map(m => ({
                    id: m.id,
                    name: m.local_name,
                    unit: m.unit,
                })),
                nodes: node.nodes.map(child => ({id: child.id, name: child.name})),
            };

            if (node.nodes.length) {
                this.addDeviceMetricNodesRecursively(nodes, node.nodes);
            }
        });
    }
}
